import { useEffect, useMemo } from 'react'

import isEmpty from 'lodash/isEmpty'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import DocumentForm from 'modules/timeoff/lib/DocumentForm'
import LeaveForm from 'modules/timeoff/lib/LeaveForm'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

interface FormProps {
  currentEmployee: Employee | null | undefined
  leaveYear: string | null | undefined
  selectedLeaveType: LeaveType | null | undefined
  startDate?: string | undefined
  endDate?: string | undefined
}
interface ReturnType {
  form: LeaveForm
  documentForm: DocumentForm
  employee: Employee | null | undefined
  year: number
}

const useForms = ({
  leaveYear,
  selectedLeaveType,
  currentEmployee,
  startDate,
  endDate,
}: FormProps): ReturnType => {
  const documentForm = useMemo(() => new DocumentForm({}), [])
  const currentEmployeeId = currentEmployee?.attributes.id

  const date = useMemo(() => {
    const today = moment()
    const start = startDate ? moment(startDate) : today

    return leaveYear ? today.year(+leaveYear) : start
  }, [leaveYear, startDate])

  const form = useMemo(() => {
    const startOn = dateToString(date, 'backend')
    const finishOn = endDate ? dateToString(moment(endDate), 'backend') : startOn

    return new LeaveForm({
      employee_id: currentEmployeeId,
      start_on: startOn,
      finish_on: finishOn,
    })
  }, [date, currentEmployeeId, endDate])

  useEffect(() => {
    form.setValues({
      leave_type_id: selectedLeaveType?.attributes.id,
    })
  }, [form, selectedLeaveType])

  const employeeId = form.get('employee_id').value

  const employee = useMemo(() => {
    if (isEmpty(employeeId) && currentEmployee) return currentEmployee

    return employees.find({
      id: +employeeId,
    })
  }, [currentEmployee, employeeId])

  return {
    form,
    documentForm,
    employee,
    year: date.year(),
  }
}

export default useForms
